import {ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnInit, Output} from '@angular/core';
import {DropdownModule} from 'primeng/dropdown';
import {FormsModule} from '@angular/forms';
import {NgClass, NgForOf, NgIf} from '@angular/common';
import {InputTextModule} from 'primeng/inputtext';
import {ButtonModule} from 'primeng/button';
import {DividerModule} from 'primeng/divider';
import {Field, Filter, GroupField, InfoView, Query} from '../filter.type';
import {ConfirmationService, SelectItem, TreeNode} from 'primeng/api';
import {ClassicTreeSave} from '../filter-preset/filter-preset.component';
import {TableMetaComponent} from '../../table-data/table-meta/table-meta.component';
import {HavingCountTable, SaveQuery, Table} from '@tj/tj-dmp-lib-api';
import {FilterPresetService} from '../filter-preset/filter-preset.service';
import {FilterBuilderService} from '../../../services/filter-builder.service';
import {ComponentsModule} from '../../components.module';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {OperatorLabelAndOrComponent} from './operator-label-and-or/operator-label-and-or.component';
import {RippleModule} from 'primeng/ripple';
import {FilterResult} from '../filter-result.interface';
import {ConfirmDialogModule} from 'primeng/confirmdialog';

export type LogicalOperator = 'AND' | 'OR';


@Component({
    selector: 'dmp-beautiful-filter',
    templateUrl: './beautiful-filter.component.html',
    imports: [
        DropdownModule,
        FormsModule,
        NgIf,
        NgForOf,
        InputTextModule,
        ButtonModule,
        DividerModule,
        ComponentsModule,
        TranslateModule,
        OperatorLabelAndOrComponent,
        RippleModule,
        NgClass,
        ConfirmDialogModule
    ],
    styleUrls: [
        './beautiful-filter.component.scss'
        // GLOBAL : beautiful-filter-global.component.scss
    ],
    providers: [ConfirmationService]
})
export class BeautifulFilterComponent implements OnInit {

    constructor(private cdr: ChangeDetectorRef,
                private zone: NgZone,
                private confirmationService: ConfirmationService,
                private filterService: FilterPresetService,
                private filterBuilderService: FilterBuilderService,
                private readonly translate: TranslateService) {
    }

    @Input() public filters: TreeNode<Filter> = null;
    @Output() public filtersChange = new EventEmitter<TreeNode>();
    @Input() fields!: Field[] | GroupField[];
    @Input() view!: InfoView;
    @Input() query!: Query;
    @Input() tenant!: string;
    @Input() partner: string;
    @Input() commandTable!: string;
    @Input() saveQuery: ClassicTreeSave = null;
    // @Output() public saveQueryChange  = new EventEmitter<ClassicTreeSave|TreeNode>();
    @Output() public saveQueryUpdate  = new EventEmitter<FilterResult>();
    @Input() readOnly = false;
    @Input() table!: TableMetaComponent;
    @Input() displayQuerySelect = true;

    @Output() onInit = new EventEmitter<boolean>();
    @Input() tables!: Table[];

    public groups: SelectItem[] = [];
    public having: HavingCountTable[] = [];

    @Output() deleteGroup = new EventEmitter<void>();

    ngOnInit(): void {
        // Un arbre reçu en entrée (lecture seule : fiche d'une règle de tag) n'est pas remplacé par un
        // arbre vide : il ne s'affichait jamais.
        if (!this.filters) {
            this.initFilter();
        }
        //
        this.groups = [{
            label: 'ET',
            value: 'AND'
        }, {
            label: 'OU',
            value: 'OR'
        }];
        this.onInit.emit(true);
    }

    private initFilter(): void {
        this.filters = {
            data: {
                edit: true,
                typeGroup: 'OR'
            } as Filter,
            children: []
        };
        this.having = [];
    }

    public removeAll(confirm: boolean = false) {
        if (confirm) {
            this.confirmationService.confirm({
                message: this.translate.instant('components.beautiful-filter.confirmRemoveAll'),
                accept: () => {
                    this.initFilter();
                    this.saveQueryUpdate.emit({
                        tabParam: this.saveQuery,
                        filters: this.filters
                    });
                }
            });
        } else {
            this.initFilter();
        }
    }

    /**
     * Vide les filtres, y compris les conditions de comptage, et le signale à la page. Passe par
     * doChangeFilter() et non par la requête courante (`saveQuery`) : après le chargement d'un
     * préset, celle-ci porte encore le `having` du préset.
     */
    public clearFilters(): void {
        this.initFilter();
        this.doChangeFilter();
    }

    public addFilterToLevel1(): void {
        this.filterService.addGroupBlankFilter(this.filters);
        this.filtersChange.emit(this.filters);
    }

    doAddFilter(filters: TreeNode<Filter>, operator: any) {
        this.filterService.addItemBlankFilter(filters, operator);
        this.filtersChange.emit(this.filters);
    }

    doAddGroup(node: any) {
        const targetNode = this.findNode(this.filters.children, node.node);
        targetNode.children.push({
            data: {typeGroup: 'OR'},
            expanded: true,
            children: [
                {
                    data: {
                        value: null,
                        filterType: null,
                        field: null,
                        edit: true
                    } as Filter}
            ]});
        this.filtersChange.emit(this.filters);
    }


    private findNode(nodes: TreeNode[], nodeToFind): TreeNode {
        let nodeFind = null;
        nodes.find(node => {
            if (node === nodeToFind) {
                nodeFind = node;
            } else if (node.children) {
                nodeFind = this.findNode(node.children, nodeToFind);
            }
        });
        return nodeFind;
    }

    doRemoveFilter(filters: TreeNode<Filter>, filter: TreeNode<Filter>) {
        this.confirmationService.confirm({
            message: this.translate.instant('components.beautiful-filter.confirmRemoveOne'),
            accept: () => {
                filters.children = this.removeNode(filters.children, filter);
                if (filters.children.length === 0) {
                    this.filters.children = this.removeNode(this.filters.children, filters);
                }
                if (this.filters.children.length === 1){
                    this.saveQuery.value = this.filters[0];
                } else if (this.filters.children.length === 0) {
                    this.saveQuery.value = null;
                }
                this.doChangeFilter();
            }
        });
    }

    private removeFilter(filters: TreeNode<Filter>, filter: TreeNode<Filter>) {
        filters.children = this.removeNode(filters.children, filter);
        if (filters.children.length === 0) {
            this.filters.children = this.removeNode(this.filters.children, filters);
        }
        if (this.filters.children.length === 1){
            this.saveQuery.value = this.filters[0];
        } else if (this.filters.children.length === 0) {
            this.saveQuery.value = null;
        }
        this.doChangeFilter();
    }

    removeNode(nodes: TreeNode[], nodeToRemove: any): TreeNode[] {
        return nodes.filter(node => {
            if (node === nodeToRemove) {
                return false;
            } else if (node.children) {
                node.children = this.removeNode(node.children, nodeToRemove);
            }
            return true;
        });
    }

    /**
     * Vide les valeurs des filtres
     * @private
     */
    public cleanValueFilters() {
        this.confirmationService.confirm({
            message: this.translate.instant('components.beautiful-filter.confirmResetValues'),
            accept: () => {
                this.filterService.cleanValueFilter(this.filters.children);
                this.filters = this.filterService.removeFilterWithNoType(this.filters);
                this.filterService.editFalseAllFilter(this.filters.children);
                this.doChangeFilter();
            }
        });

    }

    doChangeFilter() {
        const filtre = this.filters?.children?.length > 0 ? this.filters : null;
        const saveQuery = {
            cols: [], id: '', name: '', orderings: [],
            value: filtre,
            having: this.having || [],
            partner: '',
            affectedPartners: [],
            objectQueryJson: filtre ? JSON.stringify(this.filterService.removeCircular(this.filters)) : null,
            isModel: null
        } as SaveQuery;
        const item: ClassicTreeSave = this.filterBuilderService.buildClassicTreeSaveBySaveQuery(saveQuery);
        this.saveQuery = item;
        this.saveQueryUpdate.emit({
            tabParam: this.saveQuery,
            filters: this.filters
        });

    }

    // public removeFilter(): void {
    //     if (this.filters.children.length !== 0) {
    //         this.initFilter();
    //         this.saveQuery = {
    //             cols: [],
    //             id: '',
    //             name: '',
    //             orderings: [],
    //             value: null,
    //             partner: '',
    //             affectedPartners: [],
    //             having: [],
    //             isModel: null
    //         };
    //         this.saveQueryUpdate.emit(this.saveQuery);
    //     }
    // }



    public updateFilter($event: HavingCountTable[]) {
        this.having = $event;
        this.doChangeFilter();
    }

    public setFilter(newFilters: TreeNode<Filter>): void {
        this.filters = newFilters;
        if (this.filters) {
            this.saveQuery = {
                cols: [],
                id: '',
                name: '',
                orderings: [],
                value: this.filters,
                partner: '',
                affectedPartners: [],
                having: [],
                isModel: null
            };
        } else {
            this.saveQuery = {
                cols: [],
                id: '',
                name: '',
                orderings: [],
                value: null,
                partner: '',
                affectedPartners: [],
                having: [],
                isModel: null
            };
        }
        this.saveQueryUpdate.emit({
            tabParam: this.saveQuery
        });
        this.cdr.detectChanges();
    }

    deleteChild(index: number) {
        this.filters.children.splice(index, 1);
    }

    trackByIndex(index: number): number {
        return index;
    }

    addFilterA() {
        // this.group = {
        //     type: 'group',
        //     operator: 'AND',
        //     children: [{
        //         type: 'group',
        //         operator: 'AND',
        //         children: [{
        //             type: 'filter',
        //             filter: null
        //         }]
        //     }]
        // };
        // this.newFilter = { type: 'filter', filter: {
        //         value: null,
        //         filterType: null,
        //         field: null,
        //         edit: true,
        //         options: {}
        //     } as Filter };
    }

    doDeleteFilter(filter: TreeNode<Filter>) {

    }

    public isNotModificationFilter(filters: TreeNode<Filter>): boolean {
        return filters && filters.children ? filters.children
            .map(c => c.data.edit)
            .filter(edit => edit)
            .length === 0 : true;
    }

    public doValidateGroupFilter(groupfilter: TreeNode<Filter>) {
        if (groupfilter.children) {
            groupfilter.children = groupfilter.children.filter(c => c.data.field != null);
        }
        groupfilter.data.edit = false;
        if (groupfilter.children && groupfilter.children.length === 0) {
            this.removeFilter(this.filters, groupfilter);
        }
        this.doChangeFilter();
    }


    public updateSaveQuery(saveQuery: SaveQuery) {
        const item: ClassicTreeSave = this.filterBuilderService.buildClassicTreeSaveBySaveQuery(saveQuery);
        this.filters = item.value || {
            data: {
                edit: true,
                typeGroup: 'OR'
            } as Filter,
            children: []
        };
        this.saveQuery = item;
        this.having = item.having || [];

        this.saveQueryUpdate.emit({
            tabParam: this.saveQuery,
            filters: this.filters
        });
        this.cdr.markForCheck();
        this.cdr.detectChanges();
    }
}
